import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Field } from '@/components/integrations/forms/shared';
import { integrationsApi } from '@/api/integrations';
import type { IntegrationType } from '@/api/types';
import type { WizardDraft } from './state';
import { validateMetadata } from './state';

const KIND_TO_INTEGRATION_TYPE: Record<string, IntegrationType[]> = {
  elasticsearch: ['elastic'],
  bigquery: ['bigquery'],
  api: ['api'],
};

interface Props {
  draft: WizardDraft;
  onChange: (patch: Partial<WizardDraft>) => void;
  nameLocked: boolean;
}

export function Step2Metadata({ draft, onChange, nameLocked }: Props): JSX.Element {
  const { t } = useTranslation();
  const errors = validateMetadata(draft).errors;

  const compatibleTypes = KIND_TO_INTEGRATION_TYPE[draft.kind] ?? [];
  const integrations = useQuery({
    queryKey: ['integrations'],
    queryFn: () => integrationsApi.list(),
    staleTime: 15_000,
  });

  const compatibleInstances = (integrations.data?.items ?? []).filter(
    (i) => compatibleTypes.includes(i.type) && i.enabled,
  );

  return (
    <div className="space-y-4">
      <Field
        label={t('tools.wizard.step2.name')}
        htmlFor="w-name"
        required
        hint={nameLocked ? t('tools.wizard.step2.nameHintLocked') : t('tools.wizard.step2.nameHintFree')}
      >
        <Input
          id="w-name"
          value={draft.toolName}
          onChange={(e) => onChange({ toolName: e.target.value.toLowerCase() })}
          placeholder={
            draft.kind === 'api'
              ? 'my_api_tool'
              : draft.kind === 'elasticsearch'
                ? t('tools.wizard.step2.namePlaceholderEs')
                : t('tools.wizard.step2.namePlaceholderBq')
          }
          disabled={nameLocked}
          className="font-mono text-xs"
        />
        {errors.toolName ? <p className="text-[11px] text-destructive">{errors.toolName}</p> : null}
      </Field>

      <Field
        label={t('tools.wizard.step2.description')}
        htmlFor="w-desc"
        required
        hint={t('tools.wizard.step2.descriptionHint')}
      >
        <Textarea
          id="w-desc"
          value={draft.description}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={4}
          placeholder={t('tools.wizard.step2.descriptionPlaceholder')}
        />
        {errors.description ? (
          <p className="text-[11px] text-destructive">{errors.description}</p>
        ) : null}
      </Field>

      <Field label={t('tools.wizard.step2.integration')} htmlFor="w-integration" required hint={t('tools.wizard.step2.integrationHint')}>
        {compatibleInstances.length > 0 ? (
          <select
            id="w-integration"
            value={draft.integrationId}
            onChange={(e) => onChange({ integrationId: e.target.value })}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">{t('tools.wizard.step2.integrationPlaceholder')}</option>
            {compatibleInstances.map((inst) => (
              <option key={inst.instanceId} value={inst.instanceId}>
                {inst.label} ({inst.type})
              </option>
            ))}
          </select>
        ) : (
          <div className="flex items-center gap-2 rounded border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
            <span>
              {t('tools.wizard.step2.noIntegration')}{' '}
              <Link to="/integrations" className="text-primary hover:underline">
                {t('tools.wizard.step2.addIntegrationLink')}
              </Link>
            </span>
          </div>
        )}
        {errors.integrationId ? (
          <p className="text-[11px] text-destructive">{errors.integrationId}</p>
        ) : null}
      </Field>

      <Field label={t('tools.wizard.step2.state')} htmlFor="w-enabled" hint={t('tools.wizard.step2.stateHint')}>
        <div className="flex items-center gap-2 text-sm">
          <Switch
            id="w-enabled"
            checked={draft.enabled}
            onCheckedChange={(v) => onChange({ enabled: v })}
          />
          <span>{draft.enabled ? t('tools.wizard.step2.enabled') : t('tools.wizard.step2.disabled')}</span>
        </div>
      </Field>
    </div>
  );
}
